import React, { useState, useEffect, useCallback } from 'react';
import { 
    Plus, 
    Search, 
    Edit2, 
    Trash2, 
    Loader2,
    Upload
} from 'lucide-react';
import Button from '../../components/ui/Button';
import Pagination from '../../components/ui/Pagination';
import SortableHeader from '../../components/ui/SortableHeader';
import Modal from '../../components/ui/Modal';
import MedidorForm from './MedidorForm';
import { useToast } from '../../../context/ToastContext';
import { useAuth } from '../../../context/AuthContext';
import { AdminService } from '../../../application/services/AdminService';
import DashboardLayout from '../../layouts/DashboardLayout';

const MedidoresPage = () => {
  const { isSuperAdmin, user, loading: authLoading } = useAuth();
  const [medidores, setMedidores] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [limit, setLimit] = useState(25);
  const [sortConfig, setSortConfig] = useState({ key: 'fecha', direction: 'DESC' });
  
  const [showModal, setShowModal] = useState(false);
  const [selectedMedidor, setSelectedMedidor] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Estados para Importación
  const [importing, setImporting] = useState(false);
  const [fileToImport, setFileToImport] = useState(null);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [importResults, setImportResults] = useState(null);
  
  const fileInputRef = React.useRef(null);
  
  const toast = useToast();

  const handleFileUpload = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setFileToImport(file);
      setShowImportConfirm(true);
  };

  const confirmImport = async () => {
    if (!fileToImport) return;
    
    setImporting(true);
    try {
        const result = await AdminService.importarMedidores(fileToImport);
        setImportResults(result); 
        setShowImportConfirm(false); 
        toast.success('Proceso de importación finalizado');
    } catch (error) {
        console.error(error);
        toast.error('Error al importar medidores.');
        setShowImportConfirm(false);
    } finally {
        setImporting(false);
        setFileToImport(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const closeImportResults = () => {
      setImportResults(null);
      fetchData();
  };

  const fetchData = useCallback(async (currentPage = 1, search = searchTerm, currentLimit = limit, currentSort = sortConfig) => {
    setLoading(true);
    try {
        const promises = [AdminService.getAllMedidores(currentPage, currentLimit, search, currentSort.key, currentSort.direction)];

        if (isSuperAdmin()) {
            promises.push(AdminService.getEmpresas());
        }

        const [medidoresData, empresasData] = await Promise.all(promises);
        
        setMedidores(medidoresData?.items || medidoresData || []);
        setTotalPages(medidoresData?.totalPages || 1);
        setTotalItems(medidoresData?.total || medidoresData?.count || 0);
        setPage(currentPage);
        
        if (isSuperAdmin()) {
            const listaEmpresas = Array.isArray(empresasData) ? empresasData : (empresasData?.data || []);
            setEmpresas(listaEmpresas);
        }
    } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Error al cargar datos');
    } finally {
        setLoading(false);
    }
  }, [isSuperAdmin, searchTerm, limit, sortConfig, toast]);

    useEffect(() => {
      if (!authLoading) {
          fetchData(1, searchTerm, limit, sortConfig);
      }
    }, [authLoading, user, fetchData, searchTerm, limit, sortConfig]);

  const handleOpenCreate = () => {
      setSelectedMedidor(null);
      setShowModal(true);
  };

  const handleOpenEdit = (medidor) => {
      setSelectedMedidor(medidor);
      setShowModal(true);
  };

  const handleDelete = (medidor) => {
      setSelectedMedidor(medidor);
      setShowDeleteConfirm(true);
  };

  const handleSuccess = () => {
      setShowModal(false);
      fetchData(page);
  };

  const confirmDelete = async () => {
    try {
      await AdminService.deleteMedidor(selectedMedidor.codigo_medidor);
      toast.success('Medidor eliminado exitosamente');
      fetchData(page);
    } catch (error) {
      console.error('Error deleting medidor:', error);
      toast.error('Error al eliminar medidor');
    } finally {
        setShowDeleteConfirm(false);
        setSelectedMedidor(null);
    }
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    fetchData(1, term, limit, sortConfig);
  };

  const handleSort = (key) => {
    let direction = 'ASC';
    if (sortConfig.key === key && sortConfig.direction === 'ASC') {
      direction = 'DESC';
    }
    const newSort = { key, direction };
    setSortConfig(newSort);
  };

  const handleItemsPerPageChange = (newLimit) => {
    setLimit(newLimit);
  };

  // La filtración ahora es 100% en el servidor.

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-EC');
  };

  return (
    <DashboardLayout title="Gestión de Medidores">
        <div className="space-y-6 animate-fade-in">
        
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
                <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Medidores</h1>
                <p className="text-sm text-gray-500 mt-1">
                    Administra los medidores de agua registrados.
                </p>
            </div>
            <div className="flex gap-2">
                <input 
                    type="file" 
                    ref={fileInputRef}
                    className="hidden" 
                    accept=".xlsx, .xls"
                    onChange={handleFileUpload}
                />
                <Button 
                    variant="secondary"
                    onClick={() => fileInputRef.current.click()} 
                    className="flex items-center gap-2"
                    disabled={importing}
                >
                    {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    Importar Excel
                </Button>
                <Button onClick={handleOpenCreate} className="flex items-center gap-2 bg-blue-600 shadow-sm hover:bg-blue-700">
                    <Plus className="w-4 h-4" />
                    Nuevo Medidor
                </Button>
            </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 mb-6 flex items-center">
            <Search className="w-5 h-5 text-gray-400 mr-3" />
            <input 
                type="text" 
                placeholder="Buscar por código, nombre, cliente, ubicación, empresa..."
                className="flex-1 outline-none text-gray-600 placeholder-gray-400"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
            />
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-gray-50 border-b border-gray-100">
                            <SortableHeader label="Código" field="codigo" currentSort={sortConfig.key} currentOrder={sortConfig.direction} onSort={handleSort} />
                            {isSuperAdmin() && <th className="px-4 py-3 text-xs uppercase font-bold text-gray-500 tracking-wider">Empresa</th>}
                            <th className="px-4 py-3 text-xs uppercase font-bold text-gray-500 tracking-wider">Propietario</th>
                            <SortableHeader label="Nombre" field="nombre" currentSort={sortConfig.key} currentOrder={sortConfig.direction} onSort={handleSort} />
                            <SortableHeader label="Ubicación" field="ubicacion" currentSort={sortConfig.key} currentOrder={sortConfig.direction} onSort={handleSort} />
                            <SortableHeader label="F. Instalación" field="fecha" currentSort={sortConfig.key} currentOrder={sortConfig.direction} onSort={handleSort} />
                            <th className="px-4 py-3 text-xs uppercase font-bold text-gray-500 tracking-wider">Tarifa</th>
                            <SortableHeader label="Estado" field="estado" currentSort={sortConfig.key} currentOrder={sortConfig.direction} onSort={handleSort} />
                            <th className="px-4 py-3 text-right text-xs uppercase font-bold text-gray-500 tracking-wider">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {loading ? (
                            <tr>
                                <td colSpan={isSuperAdmin() ? 10 : 9} className="px-6 py-8 text-center text-gray-500">
                                    <div className="flex justify-center items-center">
                                        <Loader2 className="w-6 h-6 animate-spin mr-2" />
                                        Cargando medidores...
                                    </div>
                                </td>
                            </tr>
                        ) : medidores.length === 0 ? (
                            <tr>
                                <td colSpan={isSuperAdmin() ? 10 : 9} className="px-6 py-8 text-center text-gray-500">
                                    No se encontraron medidores
                                </td>
                            </tr>
                        ) : (
                            medidores.map((m) => (
                                <tr key={m.id_medidor} className="hover:bg-gray-50 transition-colors border-b border-gray-100">
                                    <td className="px-4 py-1.5 text-sm text-gray-900 font-mono font-medium">
                                        {m.codigo_medidor}
                                        {m.es_comunal && (
                                            <span className="ml-2 inline-flex items-center px-1 py-0 rounded text-[9px] font-bold bg-purple-100 text-purple-700 uppercase">
                                                Comunal
                                            </span>
                                        )}
                                    </td>
                                    {isSuperAdmin() && (
                                        <td className="px-4 py-1.5 text-sm text-gray-600">
                                            {m.Empresa?.nombre || 'N/A'}
                                        </td>
                                    )}
                                    <td className="px-4 py-1.5 text-sm text-gray-700 font-medium">
                                        {m.cliente ? `${m.cliente.nombres} ${m.cliente.apellidos}` : 'Sin asignar'}
                                    </td>
                                    <td className="px-4 py-1.5 text-sm text-gray-900">{m.nombre_medidor || '-'}</td>
                                    <td className="px-4 py-1.5 text-sm text-gray-600">{m.ubicacion_medidor || '-'}</td>
                                    <td className="px-4 py-1.5 text-sm text-gray-600 whitespace-nowrap">{formatDate(m.fecha_instalacion)}</td>
                                    <td className="px-4 py-1.5">
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                            m.tarifa?.tipo_tarifa === 'Domestica' 
                                            ? 'bg-green-50 text-green-700 border border-green-100' 
                                            : 'bg-blue-50 text-blue-700 border border-blue-100'
                                        }`}>
                                            {m.tarifa?.tipo_tarifa || 'N/A'}
                                        </span>
                                    </td>
                                    <td className="px-4 py-1.5 text-center">
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                            m.estado_medidor === 'activo' 
                                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' 
                                            : 'bg-red-50 text-red-700 border border-red-100'
                                        }`}>
                                            {m.estado_medidor}
                                        </span>
                                    </td>
                                    <td className="px-4 py-1.5 text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            <button 
                                                onClick={() => handleOpenEdit(m)}
                                                className="text-gray-400 hover:text-blue-600 transition-colors p-1"
                                                title="Editar"
                                            >
                                                <Edit2 className="w-4 h-4" />
                                            </button>
                                            <button 
                                                onClick={() => handleDelete(m)}
                                                className="text-gray-400 hover:text-red-600 transition-colors p-1"
                                                title="Eliminar"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <Pagination 
                currentPage={page}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={limit}
                onPageChange={(p) => fetchData(p, searchTerm, limit, sortConfig)}
                onItemsPerPageChange={handleItemsPerPageChange}
                loading={loading}
            />
        </div>

        {showModal && (
            <MedidorForm 
                onClose={() => setShowModal(false)}
                onSuccess={handleSuccess}
                initialData={selectedMedidor}
                empresas={empresas}
                isSuperAdmin={isSuperAdmin()}
                currentUserEmpresaId={user?.id_empresa}
            />
        )}

        {/* Modal de Confirmación de Importación */}
        <Modal
            isOpen={showImportConfirm}
            onClose={() => {
                setShowImportConfirm(false);
                setFileToImport(null);
                if(fileInputRef.current) fileInputRef.current.value = '';
            }}
            title="Confirmar Importación"
            size="sm"
            footer={
                <>
                    <Button variant="secondary" onClick={() => {
                        setShowImportConfirm(false);
                        setFileToImport(null);
                        if(fileInputRef.current) fileInputRef.current.value = '';
                    }}>
                        Cancelar
                    </Button>
                    <Button 
                        className="bg-blue-600 hover:bg-blue-700" 
                        onClick={confirmImport}
                        disabled={importing}
                    >
                        {importing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        {importing ? 'Procesando...' : 'Sí, Importar'}
                    </Button>
                </>
            }
        >
            <div className="text-gray-600">
                <p>¿Estás seguro de que deseas importar el archivo <strong>{fileToImport?.name}</strong>?</p>
                <p className="text-sm mt-2 text-blue-600">Se procesarán los medidores. Los registros duplicados por código serán omitidos.</p>
            </div>
        </Modal>

        {/* Modal de Resultados de Importación */}
        <Modal
            isOpen={!!importResults}
            onClose={closeImportResults}
            title="Resultados de Importación"
            size="md"
            footer={
                <Button className="bg-blue-600 hover:bg-blue-700" onClick={closeImportResults}>
                    Cerrar y Actualizar
                </Button>
            }
        >
            {importResults && (
                <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-4 text-center">
                        <div className="bg-green-50 p-3 rounded-lg border border-green-100">
                            <div className="text-2xl font-bold text-green-600">{importResults.imported}</div>
                            <div className="text-xs text-green-700 uppercase font-semibold">Importados</div>
                        </div>
                        <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-100">
                            <div className="text-2xl font-bold text-yellow-600">{importResults.skips}</div>
                            <div className="text-xs text-yellow-700 uppercase font-semibold">Duplicados</div>
                        </div>
                        <div className="bg-red-50 p-3 rounded-lg border border-red-100">
                            <div className="text-2xl font-bold text-red-600">{importResults.errors.length}</div>
                            <div className="text-xs text-red-700 uppercase font-semibold">Errores</div>
                        </div>
                    </div>
                    
                    {importResults.errors.length > 0 && (
                        <div className="mt-4">
                            <h4 className="font-semibold text-red-700 mb-2 text-sm">Detalle de Errores:</h4>
                            <div className="bg-gray-50 p-3 rounded border border-gray-200 max-h-40 overflow-y-auto text-xs font-mono text-gray-700">
                                {importResults.errors.map((err, idx) => (
                                    <div key={idx} className="mb-1 border-b border-gray-100 pb-1 last:border-0">{err}</div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </Modal>

        <Modal
            isOpen={showDeleteConfirm}
            onClose={() => setShowDeleteConfirm(false)}
            title="Eliminar Medidor"
            size="sm"
            footer={
                <>
                    <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
                        Cancelar
                    </Button>
                    <Button 
                        className="bg-red-600 hover:bg-red-700" 
                        onClick={confirmDelete}
                    >
                        Eliminar
                    </Button>
                </>
            }
        >
            <div className="text-gray-600">
                <p>¿Estás seguro de que deseas eliminar el medidor <strong>{selectedMedidor?.codigo_medidor}</strong>?</p>
                <p className="text-sm mt-2 text-red-500">Esta acción no se puede deshacer.</p>
            </div>
        </Modal>

        </div>
    </DashboardLayout>
  );
};
export default MedidoresPage;
